import React, { useState, useEffect } from 'react';
import { Drawer, Tabs, Descriptions, Table, Image, Spin } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import './ApplicationDetail.css';
import type { TabsProps } from 'antd';
import { useOne } from '@refinedev/core';
import { RaceApplicationDTO, YachtFileDTO, RatingDTO, RaceCrewDTO } from '@/types/race/models';
import { supabaseClient } from '../../config/supabase';
import dayjs from 'dayjs';

interface ApplicationDetailProps {
  applicationId: string | null;
  onClose: () => void;
}

// ファイルの種類を判定するヘルパー関数
const isImageFile = (path: string) => {
  const ext = path.toLowerCase().split('.').pop();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '');
};

const isPdfFile = (path: string) => {
  const ext = path.toLowerCase().split('.').pop();
  return ext === 'pdf';
};

// 元のパスを取得するヘルパー関数
const getOriginalPath = (pathData: string | YachtFileDTO | null): string => {
  if (!pathData) return '';
  
  if (typeof pathData === 'string') {
    try {
      const fileInfo = JSON.parse(pathData);
      return fileInfo.path;
    } catch {
      return pathData;
    }
  }
  
  return pathData.path;
};

// ファイルビューアーコンポーネント
const FileViewer: React.FC<{
  url: string;
  title: string;
  path: string;
}> = ({ url, title, path }) => {
  if (isImageFile(path)) {
    return (
      <Image
        width={200}
        src={url}
        alt={title}
        preview={{
          mask: '拡大表示',
        }}
      />
    );
  }

  if (isPdfFile(path)) {
    return (
      <div style={{ marginBottom: 10 }}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            color: 'inherit',
            textDecoration: 'none',
          }}
        >
          <FilePdfOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
          <span>PDFを表示</span>
        </a>
      </div>
    );
  }

  return null;
};

const getFileUrl = async (pathData: string | null) => {
  if (!pathData) return null;
  
  try {
    // Try to parse as JSON first, if fails treat as plain path
    let filePath: string;
    try {
      const fileInfo = JSON.parse(pathData);
      filePath = fileInfo.path;
    } catch {
      filePath = pathData;
    }

    console.log('Getting signed URL for path:', filePath);
    
    // 署名付きURLを取得
    const result = await supabaseClient.storage
      .from('yacht-documents')
      .createSignedUrl(filePath, 3600);
    
    if (!result.error && result.data?.signedUrl) {
      console.log('Signed URL created successfully');
      return result.data.signedUrl;
    }
    
    console.error('Failed to get signed URL:', result.error);
    return null;
  } catch (error) {
    console.error('Error getting signed URL:', error);
    return null;
  }
};

interface ExtendedRaceCrewDTO extends RaceCrewDTO {
  fullName: string;
  fullNameKana: string;
  address: string;
}

export const ApplicationDetail: React.FC<ApplicationDetailProps> = ({
  applicationId,
  onClose,
}) => {
  const [fileUrls, setFileUrls] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});

  const { data, isLoading, isError } = useOne<RaceApplicationDTO & { ratings: RatingDTO }>({
    resource: "race_applications",
    id: applicationId || "",
    queryOptions: {
      enabled: !!applicationId,
      retry: 1,
      onSuccess: (response: { data: RaceApplicationDTO }) => {
        console.log('API Response:', response);
        console.log('Ratings data:', response?.data?.ratings);
      },
      onError: (error) => {
        console.error('Error fetching application details:', error);
      },
    },
    meta: {
      select: `
        *,
        yacht:yachts!race_applications_yacht_id_fkey(*),
        race_crews(*),
        ratings(*)
      `
    },
  });

  useEffect(() => {
    const loadFileUrls = async () => {
      console.log('Loading file URLs, yacht data:', data?.data?.yacht);
      console.log('Application data:', data?.data);
      if (!data?.data?.yacht) return;
      const yacht = data.data.yacht;
      
      // デバッグ用のログ追加
      console.log('Raw yacht data:', yacht);
      console.log('inspection_certificate_front:', yacht.inspection_certificate_front);
      console.log('inspection_certificate_front type:', typeof yacht.inspection_certificate_front);
      
      const parseFilePath = (fileData: YachtFileDTO | string | null | undefined) => {
        if (!fileData) return null;
        try {
          let path: string | null = null;
          
          // JSONパースを試行
          if (typeof fileData === 'string') {
            const parsed = JSON.parse(fileData);
            path = parsed.path;
          } else if ('path' in fileData) {
            path = fileData.path;
          }

          return path;
        } catch (e) {
          console.error('Error parsing file path:', e);
          return null;
        }
      };

      const files = [
        { key: 'inspection_certificate_front', path: parseFilePath(yacht.inspection_certificate_front) },
        { key: 'inspection_certificate_back', path: parseFilePath(yacht.inspection_certificate_back) },
        { key: 'inspection_logbook_front', path: parseFilePath(yacht.inspection_logbook_front) },
        { key: 'inspection_logbook_back', path: parseFilePath(yacht.inspection_logbook_back) },
        { key: 'insurance_certificate', path: parseFilePath(yacht.insurance_certificate) },
        { key: 'irc_certificate_page1', path: parseFilePath(yacht.irc_certificate_page1) },
        { key: 'irc_certificate_page2', path: parseFilePath(yacht.irc_certificate_page2) },
      ];
      
      console.log('Files to load:', files);

      for (const file of files) {
        if (file.path) {
          setLoading(prev => ({ ...prev, [file.key]: true }));
          try {
            const url = await getFileUrl(file.path);
            if (url) {
              setFileUrls(prev => ({ ...prev, [file.key]: url }));
            }
          } finally {
            setLoading(prev => ({ ...prev, [file.key]: false }));
          }
        }
      }
    };

    loadFileUrls();
  }, [data?.data]);

  if (isError) {
    return (
      <Drawer
        title="申込詳細情報"
        width="80vw"
        open={!!applicationId}
        onClose={onClose}
        destroyOnClose
        styles={{
          body: { padding: '24px' },
          content: { width: '80vw' },
          wrapper: { width: '80vw !important' }
        }}
      >
        <div>データの取得に失敗しました。再度お試しください。</div>
      </Drawer>
    );
  }

  console.log('Application data:', data?.data);
  console.log('Ratings:', data?.data?.ratings);

  const application = data?.data;

  if (isLoading) {
    return (
      <Drawer
        title="申込詳細情報"
        width="80vw"
        open={!!applicationId}
        onClose={onClose}
        destroyOnClose
        styles={{
          body: { padding: '24px' },
          content: { width: '80vw' },
          wrapper: { width: '80vw !important' }
        }}
      >
        <div>読み込み中...</div>
      </Drawer>
    );
  }

  if (!application) {
    return (
      <Drawer
        title="申込詳細情報"
        width="80vw"
        open={!!applicationId}
        onClose={onClose}
        destroyOnClose
        styles={{
          body: { padding: '24px' },
          content: { width: '80vw' },
          wrapper: { width: '80vw !important' }
        }}
      >
        <div>データが見つかりません</div>
      </Drawer>
    );
  }

  const items: TabsProps['items'] = [
    {
      key: 'basic',
      label: '艇情報',
      children: (
        <>
          <h3>参加クラス</h3>
          <Descriptions bordered column={1}>
            <Descriptions.Item label="参加クラス">
              {application.participating_classes?.map((cls: string) => {
                switch (cls) {
                  case 'irc':
                    return 'IRCクラス';
                  case 'kyc':
                    return 'KYCレーティングクラス（オープンクラス）';
                  case 'middle':
                    return 'ミドルボートクラス';
                  default:
                    return cls;
                }
              })?.join(', ') || '-'}
            </Descriptions.Item>
          </Descriptions>

          <h3 style={{ marginTop: '24px' }}>外来艇入港申込</h3>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="外来艇" span={2}>
              {application.is_visiting_boat_nishinomiya ? '✓' : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="入港予定日時">
              {application.harbor_entry_time ? dayjs(application.harbor_entry_time).format('YYYY年MM月DD日 HH:mm') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="出港予定日時">
              {application.harbor_exit_time ? dayjs(application.harbor_exit_time).format('YYYY年MM月DD日 HH:mm') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="連絡先姓名">
              {application.liaison_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="連絡先携帯">
              {application.liaison_phone || '-'}
            </Descriptions.Item>
          </Descriptions>

          <h3 style={{ marginTop: '24px' }}>艇情報</h3>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="艇名" span={2}>
              {application.yacht_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="VHF無線">
              {application.has_vhf_radio ? 'あり' : 'なし'}
            </Descriptions.Item>
            <Descriptions.Item label="コールサイン">
              {application.call_sign || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="緊急時確認済み" span={2}>
              {application.is_emergency_contact_verified ? '✓' : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="担当者氏名">
              {application.emergency_contact_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="担当者電話">
              {application.emergency_contact_phone || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="セールNo">
              {application.sail_number || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="全長">
              {application.yacht_length ? `${application.yacht_length}m` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="艇種">
              {application.yacht_type || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="定係港">
              {application.home_port || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="船齢">
              {application.yacht_age ? `${application.yacht_age}年` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="最大搭載人数">
              {application.max_crew_count || '-'}
            </Descriptions.Item>
          </Descriptions>
        </>
      ),
    },
    {
      key: 'owner',
      label: 'オーナー情報',
      children: (
        <Descriptions bordered column={2}>
          <Descriptions.Item label="氏名" span={2}>
            {`${application.owner_last_name} ${application.owner_first_name}` || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="フリガナ" span={2}>
            {`${application.owner_last_name_kana} ${application.owner_first_name_kana}` || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="生年月日">
            {application.owner_date_of_birth ? 
              dayjs(application.owner_date_of_birth).format('YYYY年MM月DD日') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="性別">
            {application.owner_gender === 'male' ? '男性' : 
             application.owner_gender === 'female' ? '女性' : 
             application.owner_gender === 'M' ? '男性' :
             application.owner_gender === 'F' ? '女性' :
             application.owner_gender === '男性' ? '男性' :
             application.owner_gender === '女性' ? '女性' : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="住所" span={2}>
            {`〒${application.owner_postal_code} ${application.owner_prefecture}${application.owner_city}${application.owner_street}${application.owner_building || ''}` || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="電話番号">
            {application.owner_phone || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="メールアドレス">
            {application.owner_email || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="JSAF会員番号">
            {application.owner_jsaf_member_no || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="IRC証書">
            {application.is_owner_irc_certified ? 'あり' : 'なし'}
          </Descriptions.Item>
          <Descriptions.Item label="体重">
            {application.owner_weight ? `${application.owner_weight}kg` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="KYC会員">
            {application.owner_kyc_member_type || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="緊急連絡先" span={2}>
            {application.owner_emergency_contact_phone || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="緊急連絡先名前">
            {application.owner_emergency_contact_name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="続柄">
            {application.owner_emergency_contact_relation || '-'}
          </Descriptions.Item>
        </Descriptions>
      ),
    },

    {
      key: 'certificates',
      label: '証明書',
      children: (
        <Descriptions bordered column={1}>
          <Descriptions.Item label="船舶検査（中間検査・定期検査）">
            <div>有効期限: {application.inspection_expiry_date ? 
              dayjs(application.inspection_expiry_date).format('YYYY年MM月DD日') : '-'}</div>
            {(application.yacht?.inspection_certificate_front || application.yacht?.inspection_certificate_front?.path) && (
              <div style={{ marginBottom: '10px' }}>
                <div>船舶検査証書（表面）</div>
                {loading['inspection_certificate_front'] ? (
                  <Spin />
                ) : fileUrls['inspection_certificate_front'] ? (
                  <FileViewer
                    url={fileUrls['inspection_certificate_front']}
                    title="船舶検査証書（表面）"
                    path={getOriginalPath(application.yacht?.inspection_certificate_front)}
                  />
                ) : null}
              </div>
            )}
            {(application.yacht?.inspection_certificate_back || application.yacht?.inspection_certificate_back?.path) && (
              <div style={{ marginBottom: '10px' }}>
                <div>船舶検査証書（裏面）</div>
                {loading['inspection_certificate_back'] ? (
                  <Spin />
                ) : fileUrls['inspection_certificate_back'] ? (
                  <FileViewer
                    url={fileUrls['inspection_certificate_back']}
                    title="船舶検査証書（裏面）"
                    path={getOriginalPath(application.yacht?.inspection_certificate_back)}
                  />
                ) : null}
              </div>
            )}
            {(application.yacht?.inspection_logbook_front || application.yacht?.inspection_logbook_front?.path) && (
              <div style={{ marginBottom: '10px' }}>
                <div>船舶検査手帳（表面）</div>
                {loading['inspection_logbook_front'] ? (
                  <Spin />
                ) : fileUrls['inspection_logbook_front'] ? (
                  <FileViewer
                    url={fileUrls['inspection_logbook_front']}
                    title="船舶検査手帳（表面）"
                    path={getOriginalPath(application.yacht?.inspection_logbook_front)}
                  />
                ) : null}
              </div>
            )}
            {(application.yacht?.inspection_logbook_back || application.yacht?.inspection_logbook_back?.path) && (
              <div style={{ marginBottom: '10px' }}>
                <div>船舶検査手帳（裏面）</div>
                {loading['inspection_logbook_back'] ? (
                  <Spin />
                ) : fileUrls['inspection_logbook_back'] ? (
                  <FileViewer
                    url={fileUrls['inspection_logbook_back']}
                    title="船舶検査手帳（裏面）"
                    path={getOriginalPath(application.yacht?.inspection_logbook_back)}
                  />
                ) : null}
              </div>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="船舶保険">
            <div>有効期限: {application.insurance_expiry_date ? 
              dayjs(application.insurance_expiry_date).format('YYYY年MM月DD日') : '-'}</div>
            {(application.yacht?.insurance_certificate || application.yacht?.insurance_certificate?.path) && (
              <div style={{ marginBottom: '10px' }}>
                <div>保険証書</div>
                {loading['insurance_certificate'] ? (
                  <Spin />
                ) : fileUrls['insurance_certificate'] ? (
                  <FileViewer
                    url={fileUrls['insurance_certificate']}
                    title="保険証書"
                    path={getOriginalPath(application.yacht?.insurance_certificate)}
                  />
                ) : null}
              </div>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="IRC証書">
            <div>有効期限: {application.irc_certificate_expiry_date ? 
              dayjs(application.irc_certificate_expiry_date).format('YYYY年MM月DD日') : '未登録'}</div>
            {(application.yacht?.irc_certificate_page1 || application.yacht?.irc_certificate_page1?.path) && (
              <div style={{ marginBottom: '10px' }}>
                <div>IRC証書(1ページ)</div>
                {loading['irc_certificate_page1'] ? (
                  <Spin />
                ) : fileUrls['irc_certificate_page1'] ? (
                  <FileViewer
                    url={fileUrls['irc_certificate_page1']}
                    title="IRC証書(1ページ)"
                    path={getOriginalPath(application.yacht?.irc_certificate_page1)}
                  />
                ) : null}
              </div>
            )}
            {(application.yacht?.irc_certificate_page2 || application.yacht?.irc_certificate_page2?.path) && (
              <div style={{ marginBottom: '10px' }}>
                <div>IRC証書(2ページ)</div>
                {loading['irc_certificate_page2'] ? (
                  <Spin />
                ) : fileUrls['irc_certificate_page2'] ? (
                  <FileViewer
                    url={fileUrls['irc_certificate_page2']}
                    title="IRC証書(2ページ)"
                    path={getOriginalPath(application.yacht?.irc_certificate_page2)}
                  />
                ) : null}
              </div>
            )}
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'crews',
      label: '乗員情報',
      children: (
        <Table
          dataSource={(application.race_crews || [])
            .map((crew: RaceCrewDTO): ExtendedRaceCrewDTO => ({
              ...crew,
              fullName: `${crew.last_name} ${crew.first_name}`,
              fullNameKana: `${crew.last_name_kana} ${crew.first_name_kana}`,
              address: `〒${crew.postal_code} ${crew.prefecture}${crew.city}${crew.street}${crew.building || ''}`
            }))
            .sort((a, b) => {
              if (a.is_skipper && !b.is_skipper) return -1;
              if (!a.is_skipper && b.is_skipper) return 1;
              if (a.is_owner && !b.is_owner) return -1;
              if (!a.is_owner && b.is_owner) return 1;
              return 0;
            })}
          rowKey="id"
          scroll={{ x: 1200 }}
          size="small"
          pagination={false}
          columns={[
            {
              title: '氏名',
              key: 'name',
              fixed: 'left',
              width: 150,
              render: (_, record) => record.fullName || '-',
            },
            {
              title: 'フリガナ',
              key: 'name_kana',
              width: 150,
              render: (_, record) => record.fullNameKana || '-',
            },
            {
              title: 'オーナー',
              dataIndex: 'is_owner',
              key: 'is_owner',
              width: 100,
              render: (value) => value ? '✓' : '',
            },
            {
              title: '責任者',
              dataIndex: 'is_skipper',
              key: 'is_skipper',
              width: 100,
              render: (value) => value ? '✓' : '-',
            },
            {
              title: '生年月日',
              dataIndex: 'date_of_birth',
              key: 'date_of_birth',
              width: 130,
              render: (date) => date ? dayjs(date).format('YYYY年MM月DD日') : '-',
            },
            {
              title: '携帯',
              dataIndex: 'phone',
              key: 'phone',
              width: 150,
            },
            {
              title: 'メールアドレス',
              dataIndex: 'email',
              key: 'email',
              width: 200,
              ellipsis: true,
            },
            {
              title: 'IRC証書',
              width: 100,
              dataIndex: 'is_irc_certified',
              key: 'is_irc_certified',
              render: (value) => value ? '✓' : '-',
            },
            {
              title: '体重',
              dataIndex: 'weight',
              key: 'weight',
              width: 80,
              render: (weight) => weight ? `${weight}kg` : '-',
            },
            {
              title: '性別',
              dataIndex: 'gender',
              key: 'gender',
              width: 80,
              render: (gender) => {
                return gender === 'male' ? '男性' : 
                       gender === 'female' ? '女性' : 
                       gender === 'M' ? '男性' :
                       gender === 'F' ? '女性' :
                       gender === '男性' ? '男性' :
                       gender === '女性' ? '女性' : '-';
              },
            },
            {
              title: 'JSAF会員番号',
              dataIndex: 'jsaf_member_no',
              key: 'jsaf_member_no',
              width: 150,
            },
            {
              title: '住所',
              key: 'address',
              width: 300,
              render: (_, record) => {
                const address = [
                  record.postal_code ? `〒${record.postal_code}` : '',
                  record.prefecture,
                  record.city,
                  record.street,
                  record.building
                ].filter(Boolean).join(' ');
                return address || '-';
              },
            },
            {
              title: 'KYC会員',
              dataIndex: 'is_kyc_member',
              key: 'is_kyc_member',
              width: 100,
              render: (_, record) => record.kyc_member_type || '-',
            },
            {
              title: '緊急連絡先',
              dataIndex: 'emergency_contact_phone',
              key: 'emergency_contact_phone',
              width: 150,
            },
            {
              title: '緊急連絡先名',
              dataIndex: 'emergency_contact_name',
              key: 'emergency_contact_name',
              width: 150,
            },
            {
              title: '続柄',
              dataIndex: 'emergency_contact_relation',
              key: 'emergency_contact_relation',
              width: 100,
            },
          ]}
        />
      ),
    },
    {
      key: 'ratings',
      label: 'レーティング',
      children: (() => {
        console.log('Rating data:', data?.data?.ratings?.[0]);
        return (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="ダクロンメイン">
              {data?.data?.ratings?.has_dacron_main === true ? '○' : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="メインファラー">
              {data?.data?.ratings?.has_main_furler === true ? '○' : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="ブームファラー">
              {data?.data?.ratings?.has_boom_furler === true ? '○' : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="ダクロンジブ">
              {data?.data?.ratings?.has_dacron_jib === true ? '○' : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="ジブファラー">
              {data?.data?.ratings?.has_jib_furler === true ? '○' : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="ソリッドペラ">
              {data?.data?.ratings?.has_solid_spade === true ? '○' : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="ノンポール">
              {data?.data?.ratings?.has_non_pole === true ? '○' : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="ダブルハンド">
              {data?.data?.ratings?.has_double_handle === true ? '○' : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="水上係留艇">
              {data?.data?.ratings?.has_mooring === true ? '○' : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="電動ウインチ">
              {data?.data?.ratings?.has_electric_winch === true ? '○' : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="スピン/ジェネカーホイスト位置" span={2}>
              {(() => {
                switch (data?.data?.ratings?.spinnaker_type) {
                  case 'above_eye_point':
                    return 'アイポイントを超える位置のスピン/ジェネカー';
                  case 'fractional':
                    return 'フラクナショナル';
                  case 'non_spinnaker':
                    return 'ノンスピン/ノンジェネカー';
                  default:
                    return '-';
                }
              })()} 
            </Descriptions.Item>
          </Descriptions>
        );
      })()
    },
    {
      key: 'agreement',
      label: '誓約書',
      children: (
        <Descriptions bordered>
          <Descriptions.Item label="署名日">
            {data?.data?.agreement_signed_date ? dayjs(data.data.agreement_signed_date).format('YYYY年MM月DD日') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="オーナー、または艇責任者氏名">
            {data?.data?.agreement_signed_by || '-'}
          </Descriptions.Item>
        </Descriptions>
      ),
    },
  ];

  return (
    <Drawer
      title="申込詳細情報"
      width={720}
      open={!!applicationId}
      onClose={onClose}
      destroyOnClose
    >
      <Tabs items={items} />
    </Drawer>
  );
};
